import { Link } from "react-router-dom";
import { AlertTriangle, UserCheck, UserPlus, Users } from "lucide-react";
import { CompositionPicker, compositionSummary, sectorLabel } from "./CompositionPicker";
import { NeedsReassignmentBadge } from "./NeedsReassignmentBadge";
import { SynthesisActsButton } from "./SynthesisActsDialog";
import { Button } from "../components/ui/button";

export const isPolyEntry = (e) => e.court_type === "Πολυμελές" || /\(ΠΟΛ\)/i.test(e.pinakio_label || "") || !!e.composition;
export const pickerPinakio = (e) => ({ pinakio_code: `board-${String(e.pinakio_label || "").replace(/\s+/g, "")}`, label: e.pinakio_label });

const DAY_NAMES = ["Δευ", "Τρι", "Τετ", "Πεμ", "Παρ", "Σαβ", "Κυρ"];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const fmt = (d) => { const [y, m, dd] = d.split("-"); return `${dd}/${m}/${y}`; };

const buildWeeks = (year, month) => {
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

// Μηνιαίο πλέγμα «Προσδιορισμός»: ανά ημέρα, πόσα πινάκια/υποθέσεις περιμένουν δικαστή (κόκκινο)
export const AssignmentMonthGrid = ({ year, month, entries, selectedDate, onSelectDate }) => {
  const byDate = entries.reduce((acc, e) => { (acc[e.hearing_date] ||= []).push(e); return acc; }, {});
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="board-month-grid">
      <div className="grid grid-cols-7 bg-slate-800 text-slate-200 text-[11px] font-medium uppercase tracking-wider">
        {DAY_NAMES.map(d => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      {buildWeeks(year, month).map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-t">
          {week.map((day, di) => {
            if (!day) return <div key={di} className="min-h-[84px] bg-slate-50/60 border-r last:border-r-0" />;
            const dateIso = iso(year, month, day);
            const dayEntries = byDate[dateIso] || [];
            const pending = dayEntries.filter(e => e.unassigned_count > 0);
            const pendingCases = pending.reduce((s, e) => s + e.unassigned_count, 0);
            const isSel = selectedDate === dateIso;
            const cls = isSel ? "bg-red-100" : pending.length ? "bg-red-50/60 hover:bg-red-100/70" : dayEntries.length ? "bg-emerald-50/50 hover:bg-emerald-100/60" : "hover:bg-slate-50";
            return (
              <button key={di} type="button" onClick={() => onSelectDate(dayEntries.length ? dateIso : null)}
                      className={`min-h-[84px] text-left p-1.5 border-r last:border-r-0 transition-colors ${cls} ${pending.length ? "shadow-[inset_0_0_0_2px_#dc2626]" : ""}`}
                      data-testid={`board-day-${dateIso}`} data-pending={pending.length}>
                <div className={`text-xs font-semibold mb-1 ${dateIso === today ? "text-blue-700" : pending.length ? "text-red-800" : dayEntries.length ? "text-emerald-800" : "text-slate-400"}`}>{day}</div>
                {pending.length > 0 && (
                  <div className="text-[10px] leading-tight rounded border-2 border-red-600 bg-white text-red-700 font-semibold px-1 py-0.5 flex items-center gap-1"
                       title={pending.map(e => `${e.pinakio_label} (${e.unassigned_count}/${e.case_count})`).join(", ")}
                       data-testid={`board-day-pending-${dateIso}`}>
                    <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                    <span>{pending.length} πινάκι{pending.length === 1 ? "ο" : "α"} · {pendingCases} υποθ.</span>
                  </div>
                )}
                {pending.slice(0, 2).map((e, i) => (
                  <div key={i} className="text-[10px] font-mono text-red-800 truncate mt-0.5">{e.pinakio_label}</div>
                ))}
                {pending.length > 2 && <div className="text-[10px] text-red-700">+{pending.length - 2} ακόμη</div>}
                {!pending.length && dayEntries.length > 0 && (
                  <div className="text-[10px] text-emerald-700 flex items-center gap-1"><UserCheck className="w-2.5 h-2.5" />{dayEntries.length} πινάκια με δικαστή</div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Λίστα πινακίων/δικασίμων με τις υποθέσεις — κλικ σε υπόθεση → ορισμός δικαστή
// Πολυμελές: πρώτα «Ανάθεση σε Σύνθεση» (βήμα 1), μετά ορισμός δικαστή-μέλους (βήμα 2)
export const AssignmentEntriesList = ({ entries, privacyFn, onAssignCase, onAssignAll, onReload }) => {
  if (!entries.length) return <p className="text-sm text-slate-500 italic" data-testid="board-entries-empty">Καμία δικάσιμος με υποθέσεις χωρίς δικαστή για την επιλογή.</p>;
  return (
    <div className="space-y-3" data-testid="board-entries-list">
      {entries.map((e) => {
        const key = `${e.hearing_date}-${e.pinakio_label}`;
        const pendingIds = e.cases.filter(c => !c.assigned_judge).map(c => c.id);
        const poly = isPolyEntry(e);
        return (
          <div key={key} className={`rounded-lg bg-white overflow-hidden ${e.unassigned_count ? "border-2 border-red-600" : "border"}`}
               data-testid={`board-entry-${key}`} data-unassigned={e.unassigned_count}>
            <div className={`flex flex-wrap items-center gap-3 px-4 py-2 border-b ${e.unassigned_count ? "bg-red-50" : "bg-emerald-50"}`}>
              <span className="font-semibold text-sm text-slate-800">{fmt(e.hearing_date)}</span>
              <span className={`font-mono text-sm ${e.unassigned_count ? "text-red-800" : "text-emerald-900"}`}>{e.pinakio_label}</span>
              <span className="text-xs text-slate-500">{e.court_type}</span>
              {e.unassigned_count > 0 ? (
                <span className="text-xs font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{e.unassigned_count}/{e.case_count} χωρίς δικαστή</span>
              ) : (
                <span className="text-xs text-emerald-700 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" />{e.judges.join(", ")}</span>
              )}
              {e.unassigned_count > 0 && e.judges.length > 0 && <span className="text-[10px] text-slate-500">Ήδη ορισμένοι: {e.judges.join(", ")}</span>}
              {e.needs_reassignment_count > 0 && (
                <span className="inline-flex items-center rounded bg-red-600 text-white text-[10px] font-extrabold px-1.5 py-0.5 tracking-wide"
                      title="Υποθέσεις όπου ο Δικαστής Προδικαστικού δεν είναι μέλος της Σύνθεσης — ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ"
                      data-testid={`board-needs-reassignment-count-${key}`}>
                  {e.needs_reassignment_count} ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ
                </span>
              )}
              {pendingIds.length > 0 && (
                <Button size="sm" className="ml-auto h-7 text-xs gap-1 bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => onAssignAll(e, pendingIds)} data-testid={`board-assign-all-${key}`}>
                  <UserPlus className="w-3.5 h-3.5" />Ορισμός σε όλες ({pendingIds.length})
                </Button>
              )}
            </div>
            {poly && (
              <div className={`flex flex-wrap items-center gap-3 px-4 py-1.5 border-b text-xs ${e.composition ? "bg-violet-50" : "bg-amber-50"}`}
                   data-testid={`board-entry-composition-row-${key}`}>
                {e.composition ? (
                  <span className="text-violet-950 flex items-center gap-1 flex-wrap" title={compositionSummary(e.composition)} data-testid={`board-entry-composition-${key}`}>
                    <Users className="w-3.5 h-3.5 text-violet-700 shrink-0" />
                    <b>Σύνθεση «{e.composition.label}»</b>
                    {sectorLabel(e.composition) && <span>— {sectorLabel(e.composition)}</span>}
                    <span className="text-slate-700">· {compositionSummary(e.composition)}</span>
                    <span className="text-violet-800 font-medium">· ο δικαστής πρέπει να είναι μέλος της σύνθεσης</span>
                  </span>
                ) : (
                  <span className="text-amber-900 font-semibold flex items-center gap-1" data-testid={`board-entry-no-composition-${key}`}>
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />Βήμα 1: Ανάθεση σε Σύνθεση — προηγείται του ορισμού δικαστή (ο δικαστής πρέπει να είναι μέλος της)
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {e.composition && (
                    <SynthesisActsButton pinakioLabel={e.pinakio_label} hearingDate={e.hearing_date} compositionLabel={e.composition?.label}
                                         onDone={() => onReload?.()} testId={`board-synthesis-acts-${key}`} />
                  )}
                  <CompositionPicker pinakio={pickerPinakio(e)} dateGroup={{ date: e.hearing_date, composition: e.composition }} hideBadge onDone={() => onReload?.()} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-[100px_80px_1fr_150px_170px] gap-2 px-4 py-1 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
              <span>ΓΑΚ</span><span>ΕΑΚ</span><span>Αντικείμενο</span><span>Τομέας</span><span>Δικαστής Προδ.</span>
            </div>
            <div className="divide-y">
              {e.cases.map(c => (
                <button key={c.id} type="button"
                        onClick={() => onAssignCase(e, c)}
                        className={`w-full grid grid-cols-[100px_80px_1fr_150px_170px] gap-2 px-4 py-1.5 text-xs items-center text-left transition-colors ${c.assigned_judge ? "hover:bg-slate-50" : "hover:bg-red-50"}`}
                        title={c.assigned_judge ? "Αλλαγή δικαστή" : "Κλικ για ορισμό δικαστή"}
                        data-testid={`board-case-${c.id}`} data-assigned={c.assigned_judge ? "1" : "0"}>
                  <span className="font-mono font-semibold text-primary">{c.gak}</span>
                  <span className="font-mono text-slate-600">{c.eak || "—"}</span>
                  <span className="text-slate-700 truncate">{privacyFn ? privacyFn(c.subject) : c.subject}</span>
                  <span className="text-slate-500 truncate">{c.sector_title || "—"}</span>
                  {c.assigned_judge ? (
                    <span className="text-emerald-700 truncate flex flex-col items-start gap-0.5">
                      <span className="flex items-center gap-1 truncate"><UserCheck className="w-3 h-3 shrink-0" />{c.assigned_judge}</span>
                      <NeedsReassignmentBadge need={c.needs_reassignment} caseId={c.id} compact />
                    </span>
                  ) : (
                    <span className="text-red-700 font-semibold flex items-center gap-1"><UserPlus className="w-3 h-3 shrink-0" />Ορισμός δικαστή</span>
                  )}
                </button>
              ))}
            </div>
            <div className="px-4 py-1 text-[10px] text-slate-400 text-right"><Link to="/hearings" className="hover:underline">Προβολή στα Πινάκια</Link></div>
          </div>
        );
      })}
    </div>
  );
};
